import * as React from "react";
import { Box, Popper, Button, Stack, Grow } from "@mui/material";
import { Question } from "../../utils/CourseTree";
import { makeStyles } from "@mui/styles";
import { ReactComponent as IconRepeat } from "../../../assets/IconRepeat.svg";
import { ReactComponent as IconCheck } from "../../../assets/IconCheck.svg";

const useStyle = makeStyles((theme) => ({
  root: {},
  toggleButton: {
    padding: "0px",
    borderColor: "transparent",
    borderRadius: "8px",
    width: "123px",
    height: "42px",
    boxShadow: "none",
    fontSize: "19px",
    "&:hover": {
      borderColor: "transparent",
    },
  },
  icon: {
    fontSize: "1em",
    "&::before": {
      display: "inline-block",
      width: "14px",
      height: "16px",
    },
  },
  menuBox: {
    padding: "32px",
    marginTop: "11px",
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    boxShadow: "0px 8px 12px #0000001A",
    maxHeight: "350px",
    overflowX: "hidden",
    overflowY: "auto",
  },
  questionButton: {
    color: theme.palette.text.primary,
    padding: "8px 16px",
    width: "379px",
    height: "auto",
    borderRadius: "8px",
    backgroundColor: "transparent",
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: "1em",
    "&:focus": {
      backgroundColor: theme.palette.primary.light,
    },
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
    },
  },
  questionButtonSelected: {
    backgroundColor: `${theme.palette.primary.light} !important`,
    fontWeight: theme.typography.fontWeightBold,
  },
  statusCircle: {
    width: "18px",
    height: "18px",
    borderRadius: "50%",
    border: `2px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusCircleRetry: {
    borderWidth: "0px",
    backgroundColor: theme.palette.warning.main,
  },
  statusCircleCompleted: {
    borderWidth: "0px",
    backgroundColor: theme.palette.success.main,
  },
}));

interface WrapperQuestionsDropdownProps {
  anchorRef: HTMLButtonElement | null;
  selectedIndex: number;
  targetQuestions: Question[];
  onSelect: (question: Question) => void;
}

export const WrapperQuestionsDropdown = ({
  anchorRef,
  selectedIndex,
  targetQuestions,
  onSelect,
}: WrapperQuestionsDropdownProps) => {
  const classes = useStyle();
  const [open, setOpen] = React.useState(false);
  const focusRef = React.useRef<HTMLButtonElement | null>(null);

  const scrollToSelectedItem = () => {
    const elementId = `LE-QuestionList-${targetQuestions[selectedIndex].id}`;

    const targetElement = document.getElementById(elementId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      targetElement.focus();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setOpen((previousOpen) => !previousOpen);
  };

  const onSelectItem = (question: Question) => {
    setOpen(false);
    onSelect(question);
  };

  const renderStatus = (question: Question, index: number) => {
    // const isMastery = question.type === QuestionType.mastery;
    // const isGuided = question.type === QuestionType.guided;
    const isCompleted = question.is_question_submitted && !question.is_question_retried;
    const isPerfect = question.is_question_perfect;

    const iconStyle = { width: "14px", height: "13px", color: "white" };
    let statusCircleStatusClass = "";
    let icon = <></>;
    if (isCompleted && isPerfect) {
      icon = <IconCheck style={iconStyle} />;
      statusCircleStatusClass = classes.statusCircleCompleted;
    }
    if (isCompleted && !isPerfect) {
      icon = <IconRepeat style={iconStyle} />;
      statusCircleStatusClass = classes.statusCircleRetry;
    }

    return (
      <Box className={`${classes.statusCircle} ${statusCircleStatusClass}`}>{icon}</Box>
    );
  };

  const renderOption = (question: Question, index: number) => {
    const isSelected = index === selectedIndex;
    return (
      <Button
        onClick={() => onSelectItem(question)}
        className={`${classes.questionButton} ${
          isSelected ? classes.questionButtonSelected : ""
        }`}
        ref={isSelected ? focusRef : undefined}
        variant="contained"
        key={question.id}
        id={`LE-QuestionList-${question.id}`}>
        <Stack direction="row" sx={{ width: "100%", height: "100%" }}>
          <Stack direction="row" sx={{ minWidth: "64px" }}>
            <Box sx={{ marginRight: "12px" }}>{renderStatus(question, index)}</Box>
            <Box>{index + 1}</Box>
          </Stack>
          <Box sx={{ flexGrow: 1, textAlign: "left" }}>{question.name}</Box>
        </Stack>
      </Button>
    );
  };

  return (
    <div>
      <Button
        size="medium"
        className={classes.toggleButton}
        variant="outlined"
        sx={{ marginLeft: "32px" }}
        onClick={handleClick}
        endIcon={
          !open ? (
            <i className={`fa-solid fa-chevron-down fa-1x`}></i>
          ) : (
            <i className={`fa-solid fa-chevron-up fa-1x`}></i>
          )
        }>
        View All
      </Button>
      <Popper open={open} anchorEl={anchorRef} placement="bottom" transition>
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            timeout={350}
            in={open}
            onEntered={scrollToSelectedItem}
            style={{ transformOrigin: "50% 0 0" }}>
            <Box className={classes.menuBox} tabIndex={1}>
              <Stack
                direction="column"
                gap="8px"
                sx={targetQuestions.length > 8 ? {} : {}}>
                {targetQuestions.map((question, index) => renderOption(question, index))}
              </Stack>
            </Box>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
